import { v4 as uuid } from 'uuid';

import { Action, ActionId } from 'client/app/apps/disco-prototype/state/actions/actions';
import { IncubateAction } from 'client/app/apps/disco-prototype/state/actions/incubateAction';
import {
  LiquidAdditionId,
  MakeMixtureAction,
} from 'client/app/apps/disco-prototype/state/actions/makeMixtureAction';
import { LiquidSet } from 'client/app/apps/disco-prototype/state/liquidSet';
import { convertComponent } from 'client/app/apps/disco-prototype/state/request/convertComponent';
import { convertValue } from 'client/app/apps/disco-prototype/state/request/convertValue';
import { Step } from 'client/app/apps/disco-prototype/state/steps';
import { getValue } from 'client/app/apps/disco-prototype/state/table';
import {
  CompiledConstraintInstruction,
  CompiledInstruction,
} from 'common/types/disco/calculateVolumes';
import { Instruction, InstructionId } from 'common/types/disco/instructions';

// InstructionMap keeps track of the relationship between the generated instructions
// and the entity from the state that the instruction relates to.
// This allows us to correctly assign the response data to the appropriate part of
// the state.
type InstructionMap = {
  // relates each instruction to the actions which caused it
  actions: Record<InstructionId, ActionId>;
  // relates instructions caused by LiquidAdditions to the addition which caused it
  additions: Record<InstructionId, LiquidAdditionId>;
};

/**
 * convertActions converts the actions from each step into a series of instructions
 * which can be understood by the 'calculateVolumes' endpoint.
 *
 * It also returns handleResponse, which applies the compiled instructions returned by the
 * endpoint to the steps.
 */
export const convertActions = (
  steps: Step[],
): {
  instructions: Instruction[];
  updateSteps: (
    steps: Step[],
    newLiquidSets: LiquidSet[],
    compiled: CompiledInstruction[],
  ) => Step[];
} => {
  const instructions: Instruction[] = [];
  const mapping: InstructionMap = { actions: {}, additions: {} };
  steps.forEach(step => {
    step.actions.forEach(action => {
      convertAction(mapping, step, action).forEach(ins => {
        instructions.push(ins);
        mapping.actions[ins.id] = action.id;
      });
    });
  });

  return {
    instructions,
    updateSteps: (steps, newLiquidSets, compiled) =>
      handleResponse(steps, mapping, newLiquidSets, compiled),
  };
};

const convertAction = (
  mapping: InstructionMap,
  step: Step,
  action: Action,
): Instruction[] => {
  switch (action.type) {
    case 'makeMixture':
      return convertMakeMixture(mapping, step, action);
    case 'incubate':
      return convertIncubate(step, action);
    default: {
      const _: never = action;
      throw `unknown action ${_}`;
    }
  }
};

const handleResponse = (
  steps: Step[],
  mapping: InstructionMap,
  newLiquidSets: LiquidSet[],
  compiled: CompiledInstruction[],
): Step[] => {
  return steps.map(step => ({
    ...step,
    target: step.target ? newLiquidSets.find(ls => ls.id === step.target?.id) : undefined,
    actions: step.actions.map(action => {
      const compiledForAction = compiled.filter(
        c => mapping.actions[c.instruction] === action.id,
      );
      switch (action.type) {
        case 'makeMixture':
          return handleMakeMixture(mapping, newLiquidSets, action, compiledForAction);
        case 'incubate':
          return handleIncubate(mapping, action, compiledForAction);
        default: {
          const _: never = action;
          throw `unknown action ${_}`;
        }
      }
    }),
  }));
};

const convertMakeMixture = (
  mapping: InstructionMap,
  step: Step,
  makeMixture: MakeMixtureAction,
): Instruction[] => {
  const ins: Instruction[] = makeMixture.additions.map(addition => {
    const id = uuid();
    mapping.additions[id] = addition.id;

    const sourceLiquidName = makeMixture.source?.liquidProps.name!;
    const sourceTable = makeMixture.source?.table;

    return {
      id,
      type: 'add_liquid',
      ...(step.target ? { target: { liquidSet: step.target.id } } : {}),
      ...(makeMixture.source
        ? {
            source: {
              liquidSet: makeMixture.source.id,
              index: sourceTable!.rows.reduce<number[]>((acc, _, idx) => {
                if (
                  getValue(sourceTable, sourceLiquidName, idx) === addition.sourceName
                ) {
                  return [...acc, idx];
                }
                return acc;
              }, []),
            },
          }
        : {}),
      ...(addition.mode === 'volume' ? { volume: convertValue(addition.volume) } : {}),
    };
  });

  const hasComposition = makeMixture.finalComposition.length > 0;
  ins.push({
    id: uuid(),
    type: 'constraint',
    ...(step.target ? { target: { liquidSet: step.target.id } } : {}),
    ...(makeMixture.isConstrained
      ? { volume: convertValue(makeMixture.finalVolume) }
      : {}),
    ...(makeMixture.isConstrained && hasComposition
      ? { composition: makeMixture.finalComposition.map(convertComponent) }
      : {}),
  });
  return ins;
};

const handleMakeMixture = (
  mapping: InstructionMap,
  newLiquidSets: LiquidSet[],
  action: MakeMixtureAction,
  compiledIns: CompiledInstruction[],
): MakeMixtureAction => {
  // we expect compiled to contain
  //   * one CompiledAddLiquidInstruction for each liquid addition in action
  //   * one CompiledConstraintInstruction
  // if they're missing, it means there was an error somewhere else that the
  // user should deal with

  const additions = action.additions.map(a => {
    const compiledForAddition = compiledIns.find(
      c => mapping.additions[c.instruction] === a.id,
    );
    return {
      ...a,
      compiled:
        compiledForAddition?.type === 'add_liquid'
          ? {
              volume: compiledForAddition.volume,
              index: compiledForAddition.index,
              errors: compiledForAddition.errors,
            }
          : undefined,
    };
  });

  const constraint = compiledIns.find(c => c.type === 'constraint') as
    | CompiledConstraintInstruction
    | undefined;
  const compiled = constraint
    ? {
        finalVolume: constraint.volume,
        finalComposition: constraint.composition,
        errors: constraint.errors,
      }
    : undefined;

  return {
    ...action,
    source: action.source
      ? newLiquidSets.find(ls => ls.id === action.source?.id)
      : undefined,
    additions,
    compiled,
  };
};

const convertIncubate = (step: Step, incubate: IncubateAction): Instruction[] => {
  return [
    {
      id: uuid(),
      type: 'prompt',
      ...(step.target ? { target: { liquidSet: step.target.id } } : {}),
      message: `Incubate for ${incubate.duration} at ${incubate.temperature}`,
    },
  ];
};

const handleIncubate = (
  _: InstructionMap,
  action: IncubateAction,
  compiledIns: CompiledInstruction[],
): IncubateAction => {
  // we expect a single prompt
  const prompt = compiledIns.find(c => c.type === 'prompt');
  const compiled = prompt ? { errors: prompt.errors } : undefined;

  return {
    ...action,
    compiled,
  };
};
